// Client input layer — steps 14 + 17.
//
// Captures held WASD (move), mouse position (aim + build-cursor), left
// mouse button (fire OR place), right mouse button (break), F (use_skill),
// and B (toggle build mode); pumps one tick-numbered `InputMsg` per 30 Hz
// interval up to the network worker.
//
// 30 Hz matches the server tick rate. The server applies the latest
// input on each of its ticks; sampling at the same cadence keeps things
// aligned and is cheap (~5 small messages/sec at idle).
//
// Mode dispatch:
//   - Build mode OFF (default): LMB → fire=true. RMB → ignored.
//     buildAction stays None; weapons logic on the server gets to run.
//   - Build mode ON: LMB → buildAction=Place. RMB → buildAction=Break.
//     fire is forced false (no shooting while building). The cursor
//     world coordinates ride along with every place/break.
//
// One key bind per command, chosen to match the brief + user spec:
//   W A S D   →  move
//   F         →  toggle build mode (Fortnite-style, edge-triggered: down-only)
//   LMB       →  fire (combat) OR place (build)
//   RMB       →  break (build only)
//
// (The brief earmarks a `use_skill` flag that isn't wired to anything
// yet — kept as `false` in the outgoing payload until a skill system
// lands. No keybind reserved for it.)
//
// Build cells are CLIENT-PICKED. While LMB or RMB is held in build
// mode, the pump tracks every cell the cursor crosses (via the native
// mousemove event, which fires at the OS pointer rate — typically
// 60-200 Hz) and accumulates them into a batch. On each 30 Hz tick
// the batch is emitted alongside `buildAction` and cleared. This way
// a fast cursor sweep doesn't drop placements / breaks on the floor:
// the server sees every cell that was under the pointer, not just
// the one sampled at the tick boundary.

import type { CursorResolver } from "./renderer/viewport";
import {
  BUILD_ACTION_BREAK,
  BUILD_ACTION_NONE,
  BUILD_ACTION_PLACE,
  type BuildAction,
  type InputMsg,
} from "./worker-protocol";

const TICK_HZ = 30;
/** Exact wall-clock interval between input ticks. 1000 / 30 = 33.333…
 *  ms. NOT rounded — `Math.round(33.333) = 33` produces 30.30 inputs/sec
 *  to a 30 Hz server. Exported so main.ts's fixed-timestep render-loop
 *  accumulator uses the SAME interval to decide how many `step()` calls
 *  to drain per frame — one shared clock, no timer-vs-vsync beat. */
export const TICK_INTERVAL_MS = 1000 / TICK_HZ;

/** Held-key set we care about for inputs that map to the server. `f`
 *  is intentionally NOT in this set — it's handled separately as an
 *  edge-triggered build-mode toggle, not a held state. */
const GAMEPLAY_KEYS = new Set(["w", "a", "s", "d"]);

/** Break-AoE radius in world cells. Mirror of the server constant that
 *  used to live there pre-cell-batch; now the CLIENT applies the AoE
 *  by seeding the break batch with cells in this radius around the
 *  cursor each frame. Server just applies whatever cells arrive. */
const BREAK_AOE_RADIUS = 0.8;

/** Per-tick cap on cells we send. Matches `MAX_BUILD_CELLS_PER_INPUT`
 *  on the wire. Worst case a fast diagonal break sweep can cross 3–4
 *  cells per frame; 16 covers any realistic 33 ms window with room
 *  to spare and bounds the per-input wire payload. */
const MAX_BUILD_CELLS_PER_TICK = 16;

/** Pack a signed cell coord pair into one 32-bit number for `Set`
 *  membership dedup. Same layout as `buildPrediction.packCellKey` so
 *  keys stay comparable across modules. */
function packCellKey(x: number, y: number): number {
  return ((x & 0xffff) << 16) | (y & 0xffff);
}

/** Resolver from canvas-relative mouse to world-space aim angle. */
export type AimAngleResolver = (canvasX: number, canvasY: number) => number;

/** Sender callback — main.ts wires this to `worker.postMessage`. */
export type InputSender = (msg: InputMsg) => void;

/** Optional UI notifier — called whenever build mode toggles so the
 *  HUD can update. Not strictly required (peek() exposes the bool). */
export type BuildModeListener = (active: boolean) => void;

export class InputPump {
  private readonly target: HTMLElement;
  private readonly aim: AimAngleResolver;
  private readonly cursor: CursorResolver;
  private readonly send: InputSender;
  private readonly onBuildModeChange: BuildModeListener;

  // Mutable input state captured by event listeners.
  private readonly keys = new Set<string>();
  private mouseX = 0;
  private mouseY = 0;
  private lmb = false;
  private rmb = false;
  private buildMode = false;
  private tick = 0;
  /** True between `start()` and `stop()`. The render loop in main.ts
   *  owns the clock now and calls `step()` from its fixed-timestep
   *  accumulator; this flag just guards double-add/remove of listeners. */
  private running = false;

  /** Cell batch accumulated since the last 30 Hz send. Lives across
   *  mousemove / mousedown / mouseup events: when LMB or RMB is held
   *  in build mode every cell the cursor enters lands here. Tick
   *  drains and clears it. Set used for O(1) dedup on hot path. */
  private readonly buildBatch = new Set<number>();

  // Saved bound listeners so we can remove them in stop().
  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;
  private readonly onMouseMove: (e: MouseEvent) => void;
  private readonly onMouseDown: (e: MouseEvent) => void;
  private readonly onMouseUp: (e: MouseEvent) => void;
  private readonly onContextMenu: (e: MouseEvent) => void;
  private readonly onBlur: () => void;

  constructor(
    target: HTMLElement,
    aim: AimAngleResolver,
    cursor: CursorResolver,
    send: InputSender,
    onBuildModeChange: BuildModeListener = () => {},
  ) {
    this.target = target;
    this.aim = aim;
    this.cursor = cursor;
    this.send = send;
    this.onBuildModeChange = onBuildModeChange;

    this.onKeyDown = (e) => {
      const k = e.key.toLowerCase();
      // F toggles build mode on key-down only (Fortnite-style per brief
      // §3). Suppress repeat events so holding the key doesn't flicker
      // the mode rapidly.
      if (k === "f" && !e.repeat) {
        this.buildMode = !this.buildMode;
        // Drop any in-flight fire/break when leaving a mode so the
        // server doesn't see a "still pressed" frame for the old mode.
        if (!this.buildMode) this.rmb = false;
        this.onBuildModeChange(this.buildMode);
        e.preventDefault();
        return;
      }
      if (!GAMEPLAY_KEYS.has(k)) return;
      this.keys.add(k);
      e.preventDefault();
    };
    this.onKeyUp = (e) => {
      const k = e.key.toLowerCase();
      if (k === "f") {
        // Don't toggle on key-up — keydown already did. Just swallow.
        e.preventDefault();
        return;
      }
      if (!GAMEPLAY_KEYS.has(k)) return;
      this.keys.delete(k);
      e.preventDefault();
    };
    this.onMouseMove = (e) => {
      const rect = this.target.getBoundingClientRect();
      this.mouseX = e.clientX - rect.left;
      this.mouseY = e.clientY - rect.top;
      // Track cells crossed by the cursor while a build button is held.
      // This is what makes fast sweeps not skip — mousemove fires at
      // pointer rate (60-200 Hz), not at the 30 Hz tick boundary.
      this.captureBuildCellsAtCursor();
    };
    this.onMouseDown = (e) => {
      if (e.button === 0) {
        this.lmb = true;
        e.preventDefault();
      } else if (e.button === 2) {
        this.rmb = true;
        e.preventDefault();
      }
      // Capture the cell under the cursor on press, so a single
      // click-without-motion still emits a cell (otherwise we'd wait
      // for a mousemove that may never come).
      this.captureBuildCellsAtCursor();
    };
    this.onMouseUp = (e) => {
      if (e.button === 0) {
        this.lmb = false;
        e.preventDefault();
      } else if (e.button === 2) {
        this.rmb = false;
        e.preventDefault();
      }
    };
    // Right-click in build mode is the break action — suppress the
    // browser context menu so it doesn't pop up mid-game.
    this.onContextMenu = (e) => e.preventDefault();
    // Alt-tab / window switch can swallow the keyup or mouseup for a
    // held button. Clear all transient state on focus loss so the next
    // tick fires zero input.
    this.onBlur = () => {
      this.keys.clear();
      this.lmb = false;
      this.rmb = false;
      // Drop any cells captured but not yet sent — losing focus
      // mid-sweep shouldn't queue ghost edits when we return.
      this.buildBatch.clear();
    };
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onBlur);
    this.target.addEventListener("mousemove", this.onMouseMove);
    this.target.addEventListener("mousedown", this.onMouseDown);
    this.target.addEventListener("mouseup", this.onMouseUp);
    this.target.addEventListener("contextmenu", this.onContextMenu);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onBlur);
    this.target.removeEventListener("mousemove", this.onMouseMove);
    this.target.removeEventListener("mousedown", this.onMouseDown);
    this.target.removeEventListener("mouseup", this.onMouseUp);
    this.target.removeEventListener("contextmenu", this.onContextMenu);
    this.keys.clear();
    this.lmb = false;
    this.rmb = false;
  }

  /** Snapshot of current input state — for HUDs and the viewport (which
   *  needs `buildMode` + `rmb` to draw the break-circle preview). */
  peek(): {
    moveX: number;
    moveY: number;
    fire: boolean;
    useSkill: boolean;
    buildMode: boolean;
    placeHeld: boolean;
    breakHeld: boolean;
    mouseX: number;
    mouseY: number;
  } {
    const has = (k: string): boolean => this.keys.has(k);
    return {
      moveX: (has("d") ? 1 : 0) - (has("a") ? 1 : 0),
      moveY: (has("w") ? 1 : 0) - (has("s") ? 1 : 0),
      fire: !this.buildMode && this.lmb,
      // `useSkill` is repurposed as the secondary-fire bit. In combat
      // mode RMB triggers the equipped weapon's secondary action
      // (server: `shared::weapons::WeaponAction.secondary`). Suppressed
      // in build mode so RMB-break doesn't double-fire as a weapon.
      useSkill: !this.buildMode && this.rmb,
      buildMode: this.buildMode,
      placeHeld: this.buildMode && this.lmb,
      breakHeld: this.buildMode && this.rmb,
      mouseX: this.mouseX,
      mouseY: this.mouseY,
    };
  }

  /** Capture the cell(s) currently under the cursor into `buildBatch`,
   *  if a build button is held. Called from mousemove (every pixel of
   *  motion), mousedown (single click case), and step() (heartbeat for
   *  no-motion ticks). Set-dedup makes this safe to call ~unbounded
   *  times per tick.
   *
   *  - Place (LMB held): just the cell under the cursor. One per call.
   *  - Break (RMB held): cells within `BREAK_AOE_RADIUS` of the cursor,
   *    preserving the "soft sweep" feel the brief specifies. Typically
   *    1–4 cells depending on how close the cursor sits to cell edges. */
  private captureBuildCellsAtCursor(): void {
    if (!this.buildMode) return;
    if (!this.lmb && !this.rmb) return;
    if (this.buildBatch.size >= MAX_BUILD_CELLS_PER_TICK) return;
    const world = this.cursor(this.mouseX, this.mouseY);
    if (this.rmb) {
      // AoE expansion around the cursor — every cell whose center lies
      // within `BREAK_AOE_RADIUS` of `world`. Same math as the old
      // server-side `sweep_break`, now done on the client so the user
      // can paint over a region and have every cell included.
      const r = BREAK_AOE_RADIUS;
      const r2 = r * r;
      const minX = Math.floor(world.x - r - 0.5);
      const maxX = Math.floor(world.x + r - 0.5);
      const minY = Math.floor(world.y - r - 0.5);
      const maxY = Math.floor(world.y + r - 0.5);
      for (let cy = minY; cy <= maxY; cy++) {
        for (let cx = minX; cx <= maxX; cx++) {
          const dx = cx + 0.5 - world.x;
          const dy = cy + 0.5 - world.y;
          if (dx * dx + dy * dy > r2) continue;
          if (this.buildBatch.size >= MAX_BUILD_CELLS_PER_TICK) return;
          this.buildBatch.add(packCellKey(cx, cy));
        }
      }
    } else {
      // Place: just the one cell under the cursor.
      const cx = Math.floor(world.x);
      const cy = Math.floor(world.y);
      this.buildBatch.add(packCellKey(cx, cy));
    }
  }

  // -----------------------------------------------------------------
  // Tick — arrow form so `this` is captured at field-init time. Called
  // by main.ts's render-loop accumulator once per elapsed 33.333 ms of
  // wall-clock time (the single shared clock).
  // -----------------------------------------------------------------

  step = (): void => {
    const has = (k: string): boolean => this.keys.has(k);
    const mx = (has("d") ? 1 : 0) - (has("a") ? 1 : 0);
    const my = (has("w") ? 1 : 0) - (has("s") ? 1 : 0);
    this.tick = (this.tick + 1) >>> 0;

    // Pick build action by mode. Break beats place if both held — the
    // user spec lists RMB explicitly, so it's a deliberate choice. The
    // `fire` flag is forced false in build mode; the server is also
    // ignoring fire then, but a clean wire payload helps log analysis.
    let buildAction: BuildAction = BUILD_ACTION_NONE;
    if (this.buildMode) {
      if (this.rmb) buildAction = BUILD_ACTION_BREAK;
      else if (this.lmb) buildAction = BUILD_ACTION_PLACE;
    }

    // Heartbeat capture: even with zero mouse motion, holding the
    // button should keep emitting the cell under the cursor each tick
    // (so the server retries placement if a cell becomes empty, etc).
    // Mousemove already covered any motion since the last tick;
    // this just ensures the "no motion" case still ships a cell.
    if (buildAction !== BUILD_ACTION_NONE) {
      this.captureBuildCellsAtCursor();
    }

    // Drain the batch into a flat Int32Array. The wasm encoder takes
    // `[x0, y0, x1, y1, ...]` and the worker passes it straight through.
    // Hard cap at the wire's per-input ceiling.
    const cellCount = Math.min(this.buildBatch.size, MAX_BUILD_CELLS_PER_TICK);
    const buildCells = new Int32Array(cellCount * 2);
    let i = 0;
    for (const key of this.buildBatch) {
      if (i >= cellCount) break;
      // Sign-extend back from the packed 16-bit halves to full i32.
      const x = (key << 0) >> 16; // arithmetic shift, top 16 bits
      const lowU = key & 0xffff;
      const y = lowU & 0x8000 ? lowU | 0xffff0000 : lowU;
      buildCells[i * 2] = x;
      buildCells[i * 2 + 1] = y;
      i++;
    }
    this.buildBatch.clear();

    this.send({
      type: "input",
      tick: this.tick,
      moveX: mx as -1 | 0 | 1,
      moveY: my as -1 | 0 | 1,
      aimAngle: this.aim(this.mouseX, this.mouseY),
      fire: !this.buildMode && this.lmb,
      // Secondary fire (RMB in combat mode) — drives the equipped
      // weapon's `WeaponAction.secondary` slot server-side.
      useSkill: !this.buildMode && this.rmb,
      buildAction,
      buildCells,
    });
  };
}
